// src/pages/finance-portal/recurring-invoices/RecurringInvoices.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    PAUSED: { bg: '#fef9c3', color: '#854d0e' },
    COMPLETED: { bg: '#dbeafe', color: '#1e40af' },
    CANCELLED: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Paused', value: 'PAUSED' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' },
];

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseRruleToHuman(rrule) {
    if (!rrule) return '\u2014';
    const parts = {};
    rrule.split(';').forEach(part => {
        const [key, val] = part.split('=');
        if (key && val) parts[key.toUpperCase()] = val;
    });

    const freq = parts.FREQ || '';
    const interval = parseInt(parts.INTERVAL) || 1;

    const freqMap = {
        DAILY: interval === 1 ? 'Daily' : `Every ${interval} days`,
        WEEKLY: interval === 1 ? 'Weekly' : `Every ${interval} weeks`,
        MONTHLY: interval === 1 ? 'Monthly' : `Every ${interval} months`,
        YEARLY: interval === 1 ? 'Yearly' : `Every ${interval} years`,
    };

    let base = freqMap[freq] || freq;

    if (parts.BYMONTHDAY) {
        base += ` on day ${parts.BYMONTHDAY}`;
    }
    if (parts.BYDAY) {
        const dayMap = { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' };
        const days = parts.BYDAY.split(',').map(d => dayMap[d] || d).join(', ');
        base += ` on ${days}`;
    }
    if (parts.BYMONTH) {
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        base += ` in ${monthNames[parseInt(parts.BYMONTH)] || parts.BYMONTH}`;
    }

    return base;
}

export default function RecurringInvoices() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // Action dialogs
    const [pauseDialog, setPauseDialog] = useState({ open: false, item: null });
    const [resumeDialog, setResumeDialog] = useState({ open: false, item: null });
    const [cancelDialog, setCancelDialog] = useState({ open: false, item: null });
    const [acting, setActing] = useState(false);

    // Process now
    const [processing, setProcessing] = useState(false);

    const fetchItems = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/recurring-invoices', { params });
            if (res.data.success) {
                setItems(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load recurring invoices');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handlePause = async () => {
        setActing(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/recurring-invoices/${pauseDialog.item.id}/pause`, {});
            setPauseDialog({ open: false, item: null });
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to pause recurring invoice');
        } finally {
            setActing(false);
        }
    };

    const handleResume = async () => {
        setActing(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/recurring-invoices/${resumeDialog.item.id}/resume`, {});
            setResumeDialog({ open: false, item: null });
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resume recurring invoice');
        } finally {
            setActing(false);
        }
    };

    const handleCancel = async () => {
        setActing(true);
        setError(null);
        try {
            await axiosInstance.delete(`/api/finance/recurring-invoices/${cancelDialog.item.id}`);
            setCancelDialog({ open: false, item: null });
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel recurring invoice');
        } finally {
            setActing(false);
        }
    };

    const handleProcessNow = async () => {
        setProcessing(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/recurring-invoices/process', {});
            fetchItems();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to process recurring invoices');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusLabel = (status) => {
        if (!status) return '';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AutorenewIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Recurring Invoices</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Automate your billing schedules</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                            onClick={handleProcessNow}
                            disabled={processing}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            {processing ? 'Processing...' : 'Process Now'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/finance-portal/recurring-invoices/new')}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Create Recurring Invoice
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, idx) => { setStatusFilter(STATUS_TABS[idx].value); setPage(0); }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, textTransform: 'none', fontWeight: 600,
                            fontSize: '0.85rem', color: '#64748b', minHeight: 40,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    {STATUS_TABS.map(t => <Tab key={t.value} label={t.label} />)}
                </Tabs>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search recurring invoices..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Name', 'Customer', 'Frequency', 'Next Run', 'Last Run', 'Generated', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <AutorenewIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No recurring invoices found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => navigate('/finance-portal/recurring-invoices/new')}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Create your first recurring invoice
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : items.map(item => {
                                    const statusStyle = STATUS_COLORS[item.status] || {};
                                    const customerName = item.customer_name || item.customer_company || '\u2014';
                                    return (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {item.name || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {parseRruleToHuman(item.rrule)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(item.next_run_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(item.last_run_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {item.generated_count != null ? item.generated_count : 0}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(item.status)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => navigate(`/finance-portal/recurring-invoices/${item.id}/edit`)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {item.status === 'ACTIVE' && (
                                                        <Tooltip title="Pause">
                                                            <IconButton size="small" onClick={() => setPauseDialog({ open: true, item })} sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b' } }}>
                                                                <PauseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {item.status === 'PAUSED' && (
                                                        <Tooltip title="Resume">
                                                            <IconButton size="small" onClick={() => setResumeDialog({ open: true, item })} sx={{ color: '#94a3b8', '&:hover': { color: '#22c55e' } }}>
                                                                <PlayArrowIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(item.status === 'ACTIVE' || item.status === 'PAUSED') && (
                                                        <Tooltip title="Cancel">
                                                            <IconButton size="small" onClick={() => setCancelDialog({ open: true, item })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Pause Dialog */}
            <Dialog open={pauseDialog.open} onClose={() => setPauseDialog({ open: false, item: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Pause "{pauseDialog.item?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will pause the recurring invoice. No new invoices will be generated until it is resumed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPauseDialog({ open: false, item: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handlePause}
                        disabled={acting}
                        startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <PauseIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {acting ? 'Pausing...' : 'Pause'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resume Dialog */}
            <Dialog open={resumeDialog.open} onClose={() => setResumeDialog({ open: false, item: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Resume "{resumeDialog.item?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will resume the recurring invoice. New invoices will be generated on the next scheduled run.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setResumeDialog({ open: false, item: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleResume}
                        disabled={acting}
                        startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {acting ? 'Resuming...' : 'Resume'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Dialog */}
            <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, item: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Cancel "{cancelDialog.item?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently cancel the recurring invoice. Previously generated invoices will not be affected. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCancelDialog({ open: false, item: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Keep</Button>
                    <Button
                        variant="contained"
                        onClick={handleCancel}
                        disabled={acting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {acting ? 'Cancelling...' : 'Cancel Recurring Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
